import type React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getAdminUrl } from '@/utils/crossAppLinks';
import { PageTitle } from '@/hooks/usePageTitle';

/* ==========================================================================
   ABOUT. Short and in the first person. Who makes it is still to be
   written by the founder (docs/plans/MARKETING_SITE.md, open questions).
   ========================================================================== */

const About: React.FC = () => (
  <>
    <PageTitle title="About" />
    {/* ── Marquee hero ── */}
    <section>
      <div className="mx-auto max-w-6xl px-4 sm:px-8 page-top">
        <h1 className="font-display text-[clamp(2.5rem,6vw,4.75rem)] leading-[0.97] tracking-tight text-ink max-w-[16ch]">
          About Showliner
        </h1>
      </div>
    </section>

    {/* ── The letter ── */}
    <section className="border-t border-grey-200">
      <div className="mx-auto max-w-6xl px-4 sm:px-8 section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8 md:col-start-3 max-w-[60ch] space-y-5 text-lg text-grey-800 leading-relaxed">
            <p>
              The software available to independent venues was either built for massive
              operations or stitched together from spreadsheets and group texts.
              Neither one fit the room we kept walking into: the 200-cap brewery with
              a corner stage, the punk house in somebody's basement, the restaurant
              that does jazz on Wednesday nights.
            </p>
            <p>
              Booking a five-band bill shouldn't take a ten-thread email chain.
              The sound engineer at the door shouldn't be reading your stage plot
              off the bassist's iPhone. And a crowd that is already holding its phones
              might as well be able to do something with them.
            </p>
            <p>
              Showliner is made by Swavely Creative Co. It's early, and most of
              what has changed in it lately came from something going wrong at a
              real show. If something is wrong for you, email{' '}
              <a href="mailto:hello@showliner.io" className="text-ink underline underline-offset-4 decoration-grey-300 hover:decoration-primary-500 transition-colors">hello@showliner.io</a>{' '}
              and a person will answer.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ── Quick links + contact ── */}
    <section className="border-t border-grey-200 bg-grey-100/60">
      <div className="mx-auto max-w-6xl px-4 sm:px-8 section-tight">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <h2 className="font-display text-2xl text-ink leading-tight tracking-tight mb-4">Keep reading</h2>
            <ul className="space-y-2.5">
              <li>
                <Link to="/for-venues" className="text-base text-ink underline underline-offset-4 decoration-grey-300 hover:decoration-primary-500 transition-colors">
                  For venues
                </Link>
              </li>
              <li>
                <Link to="/for-performers" className="text-base text-ink underline underline-offset-4 decoration-grey-300 hover:decoration-primary-500 transition-colors">
                  For performers
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-base text-ink underline underline-offset-4 decoration-grey-300 hover:decoration-primary-500 transition-colors">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/discover" className="text-base text-ink underline underline-offset-4 decoration-grey-300 hover:decoration-primary-500 transition-colors">
                  Discover
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h2 className="font-display text-2xl text-ink leading-tight tracking-tight mb-4">Contact</h2>
            <p className="text-base text-grey-800 leading-relaxed max-w-[36ch]">
              Booking agencies, multi-venue operators, press, or anything the FAQ didn't cover:{' '}
              <a
                href="mailto:hello@showliner.io"
                className="text-ink underline underline-offset-4 decoration-grey-300 hover:decoration-primary-500 transition-colors"
              >
                hello@showliner.io
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ── Closing ── */}
    <section className="border-t border-grey-200">
      <div className="mx-auto max-w-6xl px-4 sm:px-8 section">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <h2 className="font-display text-2xl sm:text-3xl text-ink leading-tight tracking-tight max-w-[28ch]">
            Try the tool. Tell us what's wrong with it
          </h2>
          <Button size="lg" asChild>
            <a href={getAdminUrl('/register')}>Request access</a>
          </Button>
        </div>
      </div>
    </section>
  </>
);

export default About;
